import { FormAction } from '@anytag/shared-types';
import {
  ProposalJobDescriptionBreakDownMode,
  EngagementSocialAcccountBudgetInput,
  useUpdateEngagementInfluencersBudgetMutation,
  useUpdateEngagementSocialAccountsBudgetMutation
} from '@anytag/graphql';
import { useQueryHelper } from '@anytag/shared-hooks';
import { EngagementProposalInfluencersFormValues, EngagementProposalRowData } from '../types';
import { useEngagementProposalSearch } from './useEngagementProposalSearch';

interface Props {
  engagementId: number;
  jdMode: ProposalJobDescriptionBreakDownMode;
}

export const useUpdateInfluencerAccountsBudget = ({ jdMode, engagementId }: Props) => {
  const { t, enqueueSnackbar, invalidateRouteLoader } = useQueryHelper();
  const { resetViewMode } = useEngagementProposalSearch();
  const { callUpdateEngagementInfluencersBudget } = useUpdateEngagementInfluencersBudgetMutation({
    onCompleted: invalidateRouteLoader
  });
  const { callUpdateEngagementSocialAccountsBudget } = useUpdateEngagementSocialAccountsBudgetMutation({
    onCompleted: invalidateRouteLoader
  });

  const updateInfluencerAccountsBudget =
    (tableData: EngagementProposalRowData[]): FormAction<EngagementProposalInfluencersFormValues>['onSubmit'] =>
    async (values, { reset }) => {
      try {
        if (jdMode === ProposalJobDescriptionBreakDownMode.INFLUENCER_BREAKDOWN) {
          await callUpdateEngagementInfluencersBudget({
            variables: {
              input: {
                campaignId: engagementId,
                influencers:
                  tableData.map(({ influencer }, index) => ({
                    influencerId: influencer.id,
                    profit: Number(values[`${index}-profit`]),
                    netBudget: Number(values[`${index}-netBudget`]),
                    influencerCost: Number(values[`${index}-influencerCost`])
                  })) || []
              }
            }
          });
        } else {
          await callUpdateEngagementSocialAccountsBudget({
            variables: {
              input: {
                campaignId: engagementId,
                socialAccounts: tableData.reduce<EngagementSocialAcccountBudgetInput[]>(
                  (results, { influencer, socialAccount }, index) => {
                    if (socialAccount) {
                      return [
                        ...results,
                        {
                          influencerId: influencer.id,
                          socialAccountId: socialAccount.id,
                          socialAccountMedia: socialAccount.socialMedia,
                          profit: Number(values[`${index}-profit`]),
                          netBudget: Number(values[`${index}-netBudget`]),
                          influencerCost: Number(values[`${index}-influencerCost`])
                        }
                      ];
                    }

                    return results;
                  },
                  []
                )
              }
            }
          });
        }

        enqueueSnackbar(t('succeededUpdateWhitelist'), { variant: 'success' });
        resetViewMode();
        reset?.(values);
      } catch (error) {
        enqueueSnackbar(t(error.message), { variant: 'error' });
      }
    };

  return { updateInfluencerAccountsBudget };
};
